import type { CSSProperties } from 'react';
import type {
  DashboardDatasetDefinition,
  DashboardFilterDefinition,
  DashboardComponentDefinition,
} from '../../types/dashboard.js';
import type { ProcessedReportInfo } from '../../types/reporting.js';

export type DashboardFilterValues = Record<string, any>;
export type ReportFilterModel = Record<string, any>;
export type ReportParamValues = Record<string, unknown>;

export type ReportLinkContext = {
  filterModel: ReportFilterModel;
  paramValues: ReportParamValues;
};

export function dateToInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfQuarter(date: Date): Date {
  const month = date.getMonth();
  const quarterStart = Math.floor(month / 3) * 3;
  return new Date(date.getFullYear(), quarterStart, 1);
}

function startOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

export function presetToDateRange(preset: string): { start: string | null; end: string | null } {
  const today = new Date();
  const todayStr = dateToInputValue(today);

  if (preset === 'all_time') return { start: null, end: null };
  if (preset === 'today') return { start: todayStr, end: todayStr };
  if (preset === 'yesterday') {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    const yStr = dateToInputValue(y);
    return { start: yStr, end: yStr };
  }
  if (preset === 'past_7_days') {
    const s = new Date(today);
    s.setDate(today.getDate() - 7);
    return { start: dateToInputValue(s), end: todayStr };
  }
  if (preset === 'past_30_days') {
    const s = new Date(today);
    s.setDate(today.getDate() - 30);
    return { start: dateToInputValue(s), end: todayStr };
  }
  if (preset === 'past_90_days') {
    const s = new Date(today);
    s.setDate(today.getDate() - 90);
    return { start: dateToInputValue(s), end: todayStr };
  }
  if (preset === 'past_12_months') {
    const s = new Date(today);
    s.setFullYear(today.getFullYear() - 1);
    return { start: dateToInputValue(s), end: todayStr };
  }
  if (preset === 'this_week') {
    return { start: dateToInputValue(startOfWeek(today)), end: todayStr };
  }
  if (preset === 'this_month') {
    return { start: dateToInputValue(startOfMonth(today)), end: todayStr };
  }
  if (preset === 'this_quarter') {
    return { start: dateToInputValue(startOfQuarter(today)), end: todayStr };
  }
  if (preset === 'this_year') {
    return { start: dateToInputValue(startOfYear(today)), end: todayStr };
  }
  return { start: null, end: todayStr };
}

export function defaultFilterValue(filter: DashboardFilterDefinition): any {
  const rawDefault = filter.default;
  if (filter.type === 'toggle') return rawDefault === true;
  if (filter.type === 'date_range') {
    const preset = typeof rawDefault === 'object' && rawDefault !== null && (rawDefault as any).kind === 'preset'
      ? (rawDefault as any).value
      : 'all_time';
    const range = presetToDateRange(preset);
    return { preset, ...range };
  }
  if (filter.selection === 'multiple') return Array.isArray(rawDefault) ? rawDefault : [];
  return rawDefault ?? '';
}

export function defaultDashboardFilters(filters: DashboardFilterDefinition[]): DashboardFilterValues {
  return Object.fromEntries(
    filters
      .filter((f) => Boolean(f.id))
      .map((f) => [f.id, defaultFilterValue(f)]),
  );
}

export function componentData(component: DashboardComponentDefinition): Record<string, unknown> {
  return typeof component.data === 'object' && component.data !== null && !Array.isArray(component.data)
    ? component.data as Record<string, unknown>
    : {};
}

export function reportKeyForComponent(component: DashboardComponentDefinition): string | null {
  const data = componentData(component);
  const reportKey = component.report_key ?? data.report_key;
  return typeof reportKey === 'string' && reportKey.trim() ? reportKey : null;
}

export function dataSourceForComponent(
  component: DashboardComponentDefinition,
  datasets: Record<string, DashboardDatasetDefinition> = {},
): DashboardComponentDefinition | DashboardDatasetDefinition {
  return component.dataset && datasets[component.dataset]
    ? datasets[component.dataset]
    : component;
}

export function reportKeyForDataSource(
  source: DashboardComponentDefinition | DashboardDatasetDefinition,
): string | null {
  const reportKey = source.report_key;
  return typeof reportKey === 'string' && reportKey.trim() ? reportKey : null;
}

export function componentTitle(component: DashboardComponentDefinition): string {
  return component.title ?? component.label ?? component.id ?? 'Dashboard component';
}

export function componentGridStyle(component: DashboardComponentDefinition): CSSProperties {
  const layout = component.layout ?? {};
  const width = Number(layout.w ?? layout.width ?? 4);
  const height = Number(layout.h ?? layout.height ?? 3);
  const minUnit = component.type === 'kpi' ? 96 : component.type === 'chart' ? 110 : 118;
  const configuredMinHeight = Number(layout.min_height);
  return {
    gridColumn: `span ${Number.isFinite(width) ? Math.min(Math.max(width, 1), 12) : 4}`,
    minHeight: `${Number.isFinite(configuredMinHeight)
      ? Math.max(configuredMinHeight, 0)
      : Number.isFinite(height) ? Math.max(height, 2) * minUnit : 320}px`,
  };
}

export function optionalBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function filterLinkFor(
  source: DashboardComponentDefinition | DashboardDatasetDefinition,
  filterId: string,
): any {
  const data = 'type' in source ? componentData(source as DashboardComponentDefinition) : {};
  const links = (
    typeof source.filter_links === 'object' && source.filter_links !== null
      ? source.filter_links
      : data.filter_links
  ) as Record<string, any> | undefined;
  return links?.[filterId] ?? null;
}

function filterModelForDashboardFilter(link: any, value: any): ReportFilterModel {
  const field = typeof link === 'string' ? link : link?.field;
  if (!field) return {};

  if (typeof link?.apply_when === 'boolean' && value !== link.apply_when) return {};
  const effectiveValue = Object.prototype.hasOwnProperty.call(link ?? {}, 'filter_value')
    ? link.filter_value
    : value;

  if (effectiveValue && typeof effectiveValue === 'object' && effectiveValue.preset === 'all_time') return {};

  if (effectiveValue && typeof effectiveValue === 'object' && (effectiveValue.start || effectiveValue.end)) {
    return {
      [field]: {
        filterType: 'date',
        type: 'inRange',
        filter: effectiveValue.start,
        filterTo: effectiveValue.end,
      },
    };
  }

  if (effectiveValue === null || effectiveValue === undefined || effectiveValue === '') return {};

  if (link?.search_mode === 'fts' || link?.fts === true) {
    return {
      [field]: {
        filterType: 'text',
        type: 'plain_fts',
        filter: String(effectiveValue),
      },
    };
  }

  if (link?.match === 'overlaps') {
    const values = Array.isArray(effectiveValue) ? effectiveValue : [effectiveValue];
    return values.length ? { [field]: { filterType: 'array', type: 'overlaps', filter: values } } : {};
  }

  if (Array.isArray(effectiveValue)) {
    if (effectiveValue.length === 0) return {};
    return { [field]: { filterType: 'set', values: effectiveValue } };
  }

  if (typeof effectiveValue === 'boolean') {
    return { [field]: { filterType: 'text', type: 'equals', filter: effectiveValue } };
  }

  return {
    [field]: {
      filterType: Number.isFinite(Number(effectiveValue)) ? 'number' : 'text',
      type: 'equals',
      filter: Number.isFinite(Number(effectiveValue)) ? Number(effectiveValue) : effectiveValue,
    },
  };
}

export function reportContextForComponent(
  source: DashboardComponentDefinition | DashboardDatasetDefinition,
  report: ProcessedReportInfo,
  filterValues: DashboardFilterValues,
): ReportLinkContext {
  // Fixed source filters also apply to linked KPI queries and cannot be reset
  // by clearing user-selected dashboard filters.
  const data = 'type' in source ? componentData(source as DashboardComponentDefinition) : {};
  const fixedFilters = data.base_filter_model;
  const filterModel: ReportFilterModel = {
    ...(fixedFilters && typeof fixedFilters === 'object' && !Array.isArray(fixedFilters)
      ? fixedFilters as ReportFilterModel
      : {}),
  };
  const paramValues: ReportParamValues = {};

  report.parameters.forEach((param) => {
    if (param.default_value) paramValues[param.url_key] = param.default_value;
  });

  Object.entries(filterValues).forEach(([filterId, value]) => {
    const link = filterLinkFor(source, filterId);
    if (!link) return;

    if (link.parameters && value && typeof value === 'object') {
      const startKey = link.parameters.start;
      const endKey = link.parameters.end;
      if (value.preset === 'all_time') {
        if (startKey) paramValues[startKey] = '1900-01-01';
        if (endKey) paramValues[endKey] = '2999-12-31';
        return;
      }
      if (startKey && value.start) paramValues[startKey] = value.start;
      if (endKey && value.end) paramValues[endKey] = value.end;
      return;
    }

    if (link.parameter) {
      if (Array.isArray(value)) {
        if (value.length > 0) paramValues[String(link.parameter)] = value;
      } else if (value !== null && value !== undefined && value !== '') {
        paramValues[String(link.parameter)] = value;
      }
      return;
    }

    Object.assign(filterModel, filterModelForDashboardFilter(link, value));
  });

  return { filterModel, paramValues };
}

export function formatValue(
  value: unknown,
  fieldName: string,
  report: ProcessedReportInfo,
  options: Record<string, unknown> = {},
): string {
  const field = report.fields.find((f) => f.name === fieldName);
  if (value === null || value === undefined || value === '') return '0';
  const numeric = Number(value);
  const style = typeof options.style === 'string' ? options.style : 'auto';
  const notation = options.notation === 'compact' ? 'compact' : 'standard';
  const maximumFractionDigits = Number.isFinite(Number(options.maximum_fraction_digits))
    ? Number(options.maximum_fraction_digits)
    : field?.ui_precision ?? 0;
  const minimumFractionDigits = Number.isFinite(Number(options.minimum_fraction_digits))
    ? Number(options.minimum_fraction_digits)
    : 0;

  if ((style === 'currency' || (style === 'auto' && field?.ui_is_currency)) && Number.isFinite(numeric)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: typeof options.currency === 'string' ? options.currency : 'USD',
      notation,
      compactDisplay: options.compact_display === 'long' ? 'long' : 'short',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numeric);
  }
  if (style === 'percent' || (style === 'auto' && field?.ui_is_percentage)) {
    return `${numeric.toFixed(maximumFractionDigits)}%`;
  }
  if (style === 'duration_seconds' && Number.isFinite(numeric)) {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numeric);
    const unitDisplay = options.unit_display === 'short' ? 'short' : 'long';
    if (unitDisplay === 'short') return `${formatted}s`;
    return `${formatted} ${numeric === 1 ? 'second' : 'seconds'}`;
  }
  if (Number.isFinite(numeric)) {
    return new Intl.NumberFormat('en-US', {
      notation,
      compactDisplay: options.compact_display === 'long' ? 'long' : 'short',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numeric);
  }
  return String(value);
}

export function aggregateRows(rows: Record<string, any>[], fieldName: string, mode: string): number {
  const values = rows.map((row) => Number(row[fieldName])).filter(Number.isFinite);
  if (mode === 'count') return rows.length;
  if (values.length === 0) return 0;
  if (mode === 'avg') return values.reduce((sum, v) => sum + v, 0) / values.length;
  if (mode === 'max') return Math.max(...values);
  if (mode === 'min') return Math.min(...values);
  return values.reduce((sum, v) => sum + v, 0);
}
